<template lang="pug">
.segment-row
  .segment-selector-section
    .title-with-info
      h3 {{ `${$t('settings_tab.agent_tab.dashboard.pulse_configuration.segment')} ${index + 1}` }}
    .segment-selector
      aiq-select(
        :model-value="segment.segment_names"
        :placeholder="$t('settings_tab.agent_tab.dashboard.pulse_configuration.select_segment')"
        multiple
        collapse-tags
        collapse-tags-tooltip
        clearable
        @update:model-value="onSegmentNamesChange"
        popper-class="pulse-config-dropdown pulse-segment-checkbox-dropdown"
      )
        aiq-option(
          v-for="seg in availableSegments"
          :key="seg"
          :label="seg"
          :value="seg"
        )
          .checkbox-option
            aiq-checkbox(
              :model-value="(segment.segment_names || []).includes(seg)"
              @click.stop
            )
            span.option-label {{ seg }}

  .segment-metrics-section
    .section-header
      .title-with-info
        h3 {{ `${$t('settings_tab.agent_tab.dashboard.pulse_configuration.segment')} ${index + 1} ${$t('settings_tab.agent_tab.dashboard.pulse_configuration.segment_metrics')}` }}
      .metric-count(v-if="hasSegmentsSelected") {{ (segment.metrics || []).length }} / {{ maxMetrics }}
    .metrics-tags(v-if="hasSegmentsSelected")
      aiq-tag(
        v-for="metricId in segment.metrics"
        :key="metricId"
        closable
        type="info"
        @close="onRemoveMetric(metricId)"
      ) {{ getMetricLabel(metricId) }}
      .add-metric-dropdown(v-if="!isMetricsMaxed")
        aiq-select(
          :placeholder="$t('settings_tab.agent_tab.dashboard.pulse_configuration.add_metric')"
          clearable
          :filterable="selectConfig.filterable"
          :empty-values="selectConfig.emptyValues"
          :value-on-clear="''"
          @change="onAddMetric"
          popper-class="pulse-config-dropdown"
        )
          aiq-option(
            v-for="metric in availableMetricsForSegment"
            :key="metric.id"
            :label="metric.label"
            :value="metric.id"
          )
      .max-reached(v-else) {{ $t('settings_tab.agent_tab.dashboard.pulse_configuration.selection_limit_reached') }}
    .no-segment(v-else) {{ $t('settings_tab.agent_tab.dashboard.pulse_configuration.select_segment_to_add_metrics') }}
</template>

<script>
export default {
  name: 'PulseConfigSegment',
  props: {
    segment: {
      type: Object,
      required: true,
    },
    index: {
      type: Number,
      required: true,
    },
    availableSegments: {
      type: Array,
      required: true,
    },
    availableMetrics: {
      type: Array,
      required: true,
    },
    maxMetrics: {
      type: Number,
      default: 5,
    },
  },
  emits: ['update'],
  data() {
    return {
      selectConfig: {
        filterable: true,
        emptyValues: [''],
      },
    };
  },
  computed: {
    hasSegmentsSelected() {
      const segmentNames = this.segment.segment_names;
      return Array.isArray(segmentNames) && segmentNames.length > 0;
    },
    isMetricsMaxed() {
      return (this.segment.metrics || []).length >= this.maxMetrics;
    },
    availableMetricsForSegment() {
      const selectedMetrics = this.segment.metrics || [];
      return this.availableMetrics.filter(
        metric => !selectedMetrics.includes(metric.id),
      );
    },
  },
  methods: {
    emitUpdate(updates) {
      this.$emit('update', this.index, { ...this.segment, ...updates });
    },
    getMetricLabel(metricId) {
      const metric = this.availableMetrics.find(m => m.id === metricId);
      return metric ? metric.label : metricId;
    },
    onSegmentNamesChange(newSegmentNames) {
      const hasSegments = Array.isArray(newSegmentNames) && newSegmentNames.length > 0;
      this.emitUpdate({
        segment_names: newSegmentNames,
        metrics: hasSegments ? (this.segment.metrics || []) : [],
      });
    },
    onAddMetric(metricId) {
      const currentMetrics = this.segment.metrics || [];
      if (metricId && !currentMetrics.includes(metricId) && !this.isMetricsMaxed) {
        this.emitUpdate({ metrics: [...currentMetrics, metricId] });
      }
    },
    onRemoveMetric(metricId) {
      const currentMetrics = this.segment.metrics || [];
      this.emitUpdate({ metrics: currentMetrics.filter(id => id !== metricId) });
    },
  },
};
</script>

<style lang="scss" scoped>
@import "../../../../../../styles/aiq-variables.scss";

.segment-row {
  display: grid;
  grid-template-columns: 0.3fr 0.7fr;
  gap: 20px;
  padding-top: 20px;

  &:not(:last-of-type) {
    padding-bottom: 20px;
    border-bottom: 1px solid $aiq-bdc-input-default;
  }
}

.segment-selector-section {
  .title-with-info {
    margin-bottom: 15px;
  }
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
}

.title-with-info {
  display: flex;
  align-items: center;
  gap: 8px;

  h3 {
    margin: 0;
    font-size: 16px;
    font-weight: 600;
    color: $aiq-color-text-primary;
  }
}

.metric-count {
  font-size: 13px;
  color: $aiq-color-text-muted;
  font-weight: 500;
}

.metrics-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
  margin-bottom: 10px;
  min-height: 32px;

  ::v-deep .aiq-tag {
    margin: 0;
    background-color: $aiq-color-active;
    border-color: $aiq-color-active;
    color: white;
    height: 38px;
    padding: 0 18px;
    border-radius: 19px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 15px;
    line-height: normal;
    font-weight: 500;

    &:hover {
      background-color: darken($aiq-color-active, 10%);
      border-color: darken($aiq-color-active, 10%);
    }

    .el-tag__close {
      color: white;
      margin-left: 8px;
      margin-top: 0;
      display: flex;
      align-items: center;
    }
  }
}

.add-metric-dropdown {
  display: inline-block;
  min-width: 180px;
  position: relative;

  ::v-deep {
    .el-input--suffix .el-input__inner {
      padding-right: 30px;
    }
  }
}

.max-reached {
  color: $aiq-color-text-muted;
  font-size: 13px;
  padding: 8px 0;
  font-weight: 500;
}

.segment-selector {
  width: 100%;

  ::v-deep {
    .el-select__tags {
      flex-wrap: wrap;
      gap: 4px;
    }

    .el-tag {
      margin: 2px 0;
    }
  }
}

.no-segment {
  color: $aiq-color-text-muted;
  font-size: 14px;
  padding: 10px 0;
}
</style>

<style lang="scss">
@import "../../../../../../styles/aiq-variables-dark.scss";

[data-theme="dark"] .segment-row {
  &:not(:last-of-type) {
    border-bottom-color: $aiq-bdc-input-default;
  }

  .title-with-info h3 {
    color: $aiq-color-black;
  }

  .metric-count {
    color: white;
  }

  .max-reached {
    color: white;
  }

  .no-segment {
    color: white;
  }
}
</style>
